import clsx from 'clsx';
import { type FC, type PropsWithChildren } from 'react';
import { useMatch } from 'react-router';

import { FeatureFlag, useFeatureFlag, useFeatureFlagsReady } from '@/feature-flags';
import { PageHeader } from '@/layouts/page-header/page-header';
import { AgentChatViewportProvider, useAgentChatViewport } from '@/layouts/use-agent-chat-viewport';
import { Route } from '@/routes';

/**
 * The redesigned pages carry their own shell — a side nav with the logo,
 * profile, language and log out, and a top bar with the breadcrumb. Rendering
 * PageHeader above one would show every one of those twice: duplicate logo,
 * profile, language and log out.
 *
 * `useMatch` matches exactly, not descendants, so every redesigned route needs
 * its own entry — and one `useMatch` per route, not a `.some()` over an array
 * built at render time, because the hook count has to be stable across renders.
 *
 * Flag-lifetime code. It goes when the old pages do, together with the flag and
 * the route gates.
 */
const useSuppressPageHeader = (): boolean => {
  const ready = useFeatureFlagsReady();
  const redesignEnabled = useFeatureFlag(FeatureFlag.AppRedesign);

  const onProjectListIndex = useMatch(`/${Route.PROJECTS}`) !== null;
  const onNewProject = useMatch(`/${Route.NEW_PROJECT}`) !== null;
  const onAgentsDirectory = useMatch(`/${Route.AGENTS}`) !== null;
  const onAgents = useMatch(`/${Route.PROJECTS}/:projectId/${Route.AGENTS}`) !== null;
  const onUsers = useMatch(`/${Route.PROJECTS}/:projectId/${Route.USERS}`) !== null;
  const onSettings = useMatch(`/${Route.PROJECTS}/:projectId/${Route.SETTINGS}`) !== null;
  const onServiceListIndex = useMatch(`/${Route.PROJECTS}/:projectId/${Route.SERVICES}`) !== null;
  const onProfile = useMatch('/profile') !== null;

  return (
    // Pending counts as on: the flag reads false until the fetch lands, so a
    // targeted user would otherwise see the header appear and then vanish.
    //
    // Agents and Users are redesigned pages and carry their own chrome. The
    // Settings URL is in the group even though it only redirects — <Navigate>
    // navigates from an effect and renders null, so there is a committed frame
    // at the redirecting URL, and without this the legacy header flashes on it.
    //
    // New project is in the group for a stronger reason than the others: its
    // frames draw no chrome at all, not even a side nav — the brand rail down
    // the left edge is the whole of it.
    ((onProjectListIndex ||
      onNewProject ||
      onAgentsDirectory ||
      onAgents ||
      onUsers ||
      onSettings ||
      onProfile) &&
      (!ready || redesignEnabled)) ||
    // Its own term, not another member of that group. The service list is the
    // page this URL renders when the flag is off, so it must keep its header
    // while the flag is still pending. Only once the flag is known to be on does
    // it become a one-frame redirect to /agents worth hiding.
    (onServiceListIndex && ready && redesignEnabled)
  );
};

const RootViewport: FC<PropsWithChildren> = ({ children }) => {
  const { isAgentChatOpen, isAgentChatResizing } = useAgentChatViewport();
  const suppressPageHeader = useSuppressPageHeader();

  return (
    // Deliberately no overflow rule. The drawer is portaled to <body> and the
    // viewport below only ever shrinks, so nothing here needs clipping — and
    // `overflow-x: hidden` would compute the other axis to `auto`, making this a
    // scroll container and leaving the sticky page header resolving against a
    // scrollport that never scrolls.
    <div className="min-h-screen w-screen bg-day-light-4">
      <div
        data-agent-chat-viewport
        className={clsx(
          'flex min-h-screen w-full flex-col transition-[width]',
          isAgentChatResizing ? 'duration-0' : 'duration-300',
          isAgentChatOpen && 'md:w-[calc(100%-var(--agent-chat-width))]',
        )}
      >
        {!suppressPageHeader && <PageHeader />}
        <div className="relative flex h-full w-full flex-1 flex-col">{children}</div>
      </div>
    </div>
  );
};

export const RootLayout: FC<PropsWithChildren> = ({ children }) => (
  <AgentChatViewportProvider>
    <RootViewport>{children}</RootViewport>
  </AgentChatViewportProvider>
);
